/**
 * Modal prompts — US 01.06 overflow choice and US 01.07 death screen.
 * Pure DOM. Both are honest, unhurried dialogs: the death screen is never
 * timed; the overflow prompt defaults focus to the non-destructive option
 * and names every item that would be destroyed.
 */

interface Manifest {
  wouldDestroy: { itemId: string; qty: number }[];
  fits: { itemId: string; qty: number }[];
}

const CONFIRMED_DESTROY_KEY = 'wca_destroy_confirmed_once';

function modal(root: HTMLElement): { box: HTMLDivElement; close: () => void } {
  const overlay = document.createElement('div');
  overlay.className = 'wca-modal-overlay';
  const box = document.createElement('div');
  box.className = 'wca-modal';
  overlay.appendChild(box);
  root.appendChild(overlay);
  return { box, close: () => overlay.remove() };
}

/** Resolves 'leave' | 'destroy' | null (null = dismissed, trade cancelled). */
export function showOverflowPrompt(
  root: HTMLElement,
  manifest: Manifest,
  itemName: (id: string) => string,
): Promise<'leave' | 'destroy' | null> {
  return new Promise((resolve) => {
    const { box, close } = modal(root);

    const title = document.createElement('strong');
    title.textContent = "Your bag can't hold everything.";
    const list = document.createElement('p');
    list.textContent =
      'Would be destroyed: ' +
      manifest.wouldDestroy.map((d) => `${itemName(d.itemId)} ×${d.qty}`).join(', ');

    const leave = document.createElement('button');
    leave.className = 'wca-choice';
    leave.textContent = 'Leave them here for now';
    leave.addEventListener('click', () => {
      close();
      resolve('leave');
    });

    const destroy = document.createElement('button');
    destroy.className = 'wca-choice wca-danger';
    destroy.textContent = 'Take what fits — destroy the rest';
    destroy.addEventListener('click', () => {
      // First destructive use per browser requires a second confirmation
      // (US 01.06 asks per account; browser-local until a settings surface
      // exists — recorded in the decision log).
      if (!localStorage.getItem(CONFIRMED_DESTROY_KEY)) {
        if (!window.confirm('Destroyed items are gone forever. Really destroy them?')) return;
        localStorage.setItem(CONFIRMED_DESTROY_KEY, '1');
      }
      close();
      resolve('destroy');
    });

    box.append(title, list, leave, destroy);
    leave.focus(); // non-destructive default (US 01.06)
  });
}

export function showDeathScreen(
  root: HTMLElement,
  info: { lostItems: { itemId: string; qty: number }[]; lostMoney: number },
  itemName: (id: string) => string,
): Promise<void> {
  return new Promise((resolve) => {
    const { box, close } = modal(root);
    const title = document.createElement('strong');
    title.textContent = 'You died.';
    const lost = document.createElement('p');
    const itemText = info.lostItems.length
      ? info.lostItems.map((d) => `${itemName(d.itemId)} ×${d.qty}`).join(', ')
      : 'nothing else';
    lost.textContent =
      `Dropped where you fell (recoverable this week): ${itemText}. ` +
      `Money lost: ${info.lostMoney}. Pictures, progress and flags are safe.`;
    const okay = document.createElement('button');
    okay.className = 'wca-choice';
    okay.textContent = 'Wake up at the spawn';
    okay.addEventListener('click', () => {
      close();
      resolve();
    });
    box.append(title, lost, okay);
    okay.focus();
  });
}
